<template>
  <div class="container mx-auto px-4 py-6 space-y-6">
    <!-- Form Content -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 p-4 rounded-t-lg">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-bold text-white">
              {{ $t('Mobile.MobileLabourRecord.title') }}
            </h2>

            <p class="text-sm text-white/90 mt-1">
              {{ labour?.first_name }} {{ labour?.last_name }}
            </p>
          </div>

          <div class="text-right">
            <div class="text-sm font-medium text-white/90">
              {{ day.toLocaleDateString() }}
            </div>
          </div>
        </div>
      </div>

      <form
        class="p-4 space-y-6"
        @submit.prevent="saveRecord"
      >
        <!-- Time Details Section -->
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('Mobile.MobileLabourRecord.timeDetails') }}
            </h3>

            <div class="text-sm font-medium text-ccm-blue-100">
              {{ totalTime ? $t('Mobile.MobileLabourRecord.totalHours', { hours: totalTime.toFixed(1) }) : '' }}
            </div>
          </div>

          <!-- Night Shift Toggle -->
          <div
            v-if="!record"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <span class="text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.nightShift') }}
            </span>

            <label class="relative items-center cursor-pointer">
              <input
                v-model="toggleNightShift"
                type="checkbox"
                class="peer sr-only"
              >

              <div class="peer btn--switch-blue peer-checked:bg-ccm-blue-100 w-9 h-5 after:w-4 after:h-4" />
            </label>
          </div>

          <!-- Start Time -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.startTime') }}
            </label>

            <AppInput
              v-model="recordForm.form.start_at"
              class="form-input"
              name="start_at"
              type="time"
              data-test="start_at"
              :error="recordForm.getErrors('start_at')"
            />
          </div>

          <!-- End Time -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.endTime') }}
            </label>

            <AppInput
              v-model="recordForm.form.end_at"
              class="form-input"
              name="end_at"
              :type="endAtType"
              data-test="end_at"
              :error="recordForm.getErrors('end_at')"
            />
          </div>

          <!-- Break Options -->
          <div class="space-y-3">
            <label
              v-if="showMinorBreak || minorBreakMarked"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              :class="{ 'opacity-50': minorBreakMarked }"
            >
              <div class="flex items-center space-x-3">
                <input
                  v-if="minorBreakMarked"
                  type="checkbox"
                  disabled
                  checked
                  class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                >

                <input
                  v-else
                  v-model="recordForm.form.minor_break"
                  type="checkbox"
                  name="minor_break"
                  data-test="minor_break"
                  class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                >

                <div>
                  <span class="text-sm font-medium text-gray-700">{{ $t('Mobile.MobileLabourRecord.smokeBreak') }}</span>

                  <p
                    v-if="minorBreakMarked"
                    class="text-xs text-gray-500"
                  >{{ $t('Mobile.MobileLabourRecord.markedOnAnotherRecord') }}</p>
                </div>
              </div>
            </label>

            <label
              v-if="showMajorBreak || majorBreakMarked"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              :class="{ 'opacity-50': majorBreakMarked }"
            >
              <div class="flex items-center space-x-3">
                <input
                  v-if="majorBreakMarked"
                  type="checkbox"
                  disabled
                  checked
                  class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                >

                <input
                  v-else
                  v-model="recordForm.form.major_break"
                  type="checkbox"
                  name="major_break"
                  data-test="major_break"
                  class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                >

                <div>
                  <span class="text-sm font-medium text-gray-700">{{ $t('Mobile.MobileLabourRecord.lunchBreak') }}</span>

                  <p
                    v-if="majorBreakMarked"
                    class="text-xs text-gray-500"
                  >{{ $t('Mobile.MobileLabourRecord.markedOnAnotherRecord') }}</p>
                </div>
              </div>
            </label>

            <label class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div class="flex items-center space-x-3">
                <input
                  v-model="recordForm.form.is_public_holiday"
                  type="checkbox"
                  name="is_public_holiday"
                  data-test="is_public_holiday"
                  class="w-4 h-4 text-ccm-blue-100 border-gray-300 rounded focus:ring-ccm-blue-100"
                >

                <span class="text-sm font-medium text-gray-700">{{ $t('Mobile.MobileLabourRecord.applyPublicHolidayRate') }}</span>
              </div>
            </label>
          </div>
        </div>

        <!-- Location Details Section -->
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('Mobile.MobileLabourRecord.locationDetails') }}
          </h3>

          <!-- Area -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.area') }}
            </label>

            <AppSelect
              v-model="recordForm.form.area"
              data-test="area"
              class="form-input pr-10"
              name="area"
              :options="project?.areas"
              :error="recordForm.getErrors('area')"
            />
          </div>

          <!-- Element -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.element') }}
            </label>

            <AppSelect
              v-model="recordForm.form.element"
              data-test="element"
              class="form-input pr-10"
              name="element"
              :options="project?.elements"
              :error="recordForm.getErrors('element')"
            />
          </div>

          <!-- Activity -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.activity') }}
            </label>

            <AppSelect
              v-model="recordForm.form.activity"
              data-test="activity"
              class="form-input pr-10"
              name="activity"
              :options="project?.activities"
              :error="recordForm.getErrors('activity')"
            />
          </div>

          <!-- WBS Code -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.wbsCode') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.wbs_code_id"
              class="form-input pr-10"
              data-test="wbs_code"
              name="wbs_code_id"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.code }))"
              :error="recordForm.getErrors('wbs_code_id')"
            />
          </div>

          <!-- WBS Description -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.wbsDescription') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.wbs_code_id"
              class="form-input pr-10"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.description }))"
              name="wbs_description"
              data-test="wbs_description"
            />
          </div>
        </div>

        <!-- Variation/Delay Details Section -->
        <div
          v-if="variationOrders.length"
          class="space-y-4"
        >
          <h3 class="text-lg font-semibold text-gray-900">
            {{ $t('Mobile.MobileLabourRecord.variationDelayDetails') }}
          </h3>

          <!-- Event Number -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.eventNumber') }}
            </label>

            <div class="flex space-x-2">
              <div class="flex-1">
                <AppSelect
                  v-model.number="recordForm.form.variation_order_id"
                  class="form-input"
                  :options="variationOrders.map((order) => ({ value: order.id, label: order.code }))"
                  name="vo_number"
                  data-test="vo_number"
                  :error="recordForm.getErrors('variation_order_id')"
                />
              </div>

              <button
                v-if="recordForm.form.variation_order_id"
                class="btn-icon btn-icon-red btn-icon--bg-red p-2"
                type="button"
                @click.prevent="recordForm.resetData(['variation_order_id'])"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
              </button>
            </div>
          </div>

          <!-- Event Description -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileLabourRecord.eventDescription') }}
            </label>

            <AppSelect
              v-model.number="recordForm.form.variation_order_id"
              class="form-input"
              :options="variationOrders.map((order) => ({ value: order.id, label: order.description }))"
              name="vo_description"
              data-test="vo_description"
              :error="recordForm.getErrors('variation_order_id')"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-4 pt-6">
          <button
            type="button"
            class="btn btn--secondary-blue flex-1"
            @click.prevent="router.push({ name: 'MobileLabourDashboard', query: { day: dateAsYMD(day) } })"
          >
            {{ $t('Mobile.MobileLabourRecord.cancel') }}
          </button>

          <p
            v-if="record?.approved_at"
            class="text-center text-sm text-gray-600 flex-1 flex items-center justify-center"
          >
            {{ $t('Mobile.MobileLabourRecord.recordApprovedCannotModify') }}
          </p>

          <button
            v-else-if="projectsStore.project?.unarchived"
            type="submit"
            class="btn btn--primary-blue flex-1"
            data-test="submit_form"
          >
            {{ record ? $t('Mobile.MobileLabourRecord.update') : $t('Mobile.MobileLabourRecord.save') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, timeZone } from '@/helpers';
import { dateAsYMD, dateForInput, hoursToDate, timeForInput } from '@/helpers';
import { t } from '@/i18n';
import Labour from '@/models/Labour';
import Project from '@/models/Project';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useLabourDailyRecordStore } from '@/store/labourDailyRecord';
import { useLaboursStore } from '@/store/labours';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, ComputedRef, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  labourId: string;
  recordId?: string;
}>();

const projectsStore = useProjectsStore();
const laboursStore = useLaboursStore();
const wbsCodesStore = useWbsCodesStore();
const variationOrdersStore = useVariationOrdersStore();
const fetchApprovedVariations = useStoreApiAction(variationOrdersStore.fetchApprovedVariationOrders);

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', parseInt(props.projectId)).get();
});

laboursStore.fetchLabour(props.labourId);
wbsCodesStore.index(props.projectId);

fetchApprovedVariations.request(props.projectId);

const labour = computed(() => {
  return laboursStore.models.query().with('records', (query) => {
    query.whereId(parseInt(props.recordId));
  }).find(parseInt(props.labourId));
});

const project = computed(() => {
  return projectsStore.project;
});

const record = computed(() => {
  return labour.value?.records.length ? labour.value?.records[0] : undefined;
});

const queryParams = useRoute().query.day as string;
const day = ref(queryParams ? new Date(queryParams) : new Date());

const recordForm = useFormData({
  day: dateAsYMD(day.value),
  start_at: '',
  end_at: '',
  area: '',
  element: '',
  wbs_code_id: undefined,
  activity: '',
  comment: '',
  variation_order_id: undefined,
  is_public_holiday: false,
  minor_break: false,
  major_break: false,
  time_zone: timeZone,
});

const endAtType = ref<'time' | 'datetime-local'>('time');

watch(endAtType, () => {
  recordForm.form.end_at = '';
});

const futureRecord = ref(false);

watch(() => recordForm.form.day, (newValue) => {
  if(newValue) {
    const dayAsDate = new Date(newValue);

    if(!isNaN(dayAsDate.getTime()) && dayAsDate.getTime() > new Date().getTime()) {
      futureRecord.value = true;
    } else {
      futureRecord.value = false;
    }
  }
}, { immediate: true });

const totalTime = computed(() => {
  if(recordForm.form.start_at && recordForm.form.end_at) {
    let endAt: Date = undefined;

    if(endAtType.value === 'time') {
      endAt = new Date(`${recordForm.form.day} ${recordForm.form.end_at}`);
    } else {
      endAt = new Date(recordForm.form.end_at);
    }

    const timeDiff = Math.abs(
      new Date(`${recordForm.form.day} ${recordForm.form.start_at}`).getTime() -
        endAt.getTime(),
    ) / 3600000;

    return (Math.round(timeDiff * 2) / 2);
  }

  return undefined;
});

watch(() => recordForm.form.start_at, (newValue, oldValue) => {
  if(newValue) {
    const hourSplit = newValue.split(':');
    const hour = parseInt(hourSplit[0]);

    if(hour >= 17) {
      endAtType.value = 'datetime-local';

      nextTick(() => {
        if(!oldValue && !recordForm.form.end_at) {
          const endAt = new Date(day.value);
          const hourSplit = newValue.split(':');

          endAt.setHours(
            !props.recordId ? parseInt(hourSplit[0]) + project.value.labour_standard_shift : parseInt(hourSplit[0]) + 2,
          );

          endAt.setMinutes(parseInt(hourSplit[1]));
          recordForm.form.end_at = dateForInput(endAt);
        }
      });
    } else {
      endAtType.value = 'time';

      nextTick(() => {
        if(!oldValue && !recordForm.form.end_at) {
          const endAt = new Date(day.value);
          const hourSplit = newValue.split(':');

          endAt.setHours(
            !props.recordId ? parseInt(hourSplit[0]) + project.value.labour_standard_shift : parseInt(hourSplit[0]) + 2,
          );

          endAt.setMinutes(parseInt(hourSplit[1]));
          recordForm.form.end_at = timeForInput(endAt);
        }
      });
    }
  }
});

watch(record, (newValue) => {
  if(newValue) {
    recordForm.form.start_at = timeForInput(newValue.startAt);

    if(newValue.startAt.hour >= 17) {
      endAtType.value = 'datetime-local';

      nextTick(() => {
        recordForm.form.end_at = dateForInput(newValue.endAt);
      });
    } else {
      endAtType.value = 'time';

      nextTick(() => {
        recordForm.form.end_at = timeForInput(newValue.endAt);
      });
    }

    recordForm.setData(newValue, [
      'start_at',
      'end_at',
      'time_zone',
    ]);

    day.value = new Date(newValue.day);
    navStore.backRoute = { name: 'MobileLabourDashboard', query: { day: dateAsYMD(day.value) } };
  }
});

const labourDailyRecordsStore = useLabourDailyRecordStore();
const createRecordAction = useStoreApiAction(labourDailyRecordsStore.createRecord);
const updateRecordAction = useStoreApiAction(labourDailyRecordsStore.updateRecord);
const router = useRouter();

const showMinorBreak = computed(() => {
  let dailyTime = totalTime.value || 0;
  let minorBreak = false;

  if(record.value?.minor_break) {
    return true;
  }

  const records = labourDailyRecordsStore.models
    .where('labour_id', labour.value?.id)
    .where('day', recordForm.form.day)
    .get();

  records.forEach((record) => {
    dailyTime += record.hours;

    if(record.minor_break) {
      minorBreak = record.minor_break;
    }
  });

  return dailyTime > 3 && !minorBreak;
});

const showMajorBreak = computed(() => {
  let dailyTime = totalTime.value || 0;
  let majorBreak = false;

  if(record.value?.major_break) {
    return true;
  }

  const records = labourDailyRecordsStore.models
    .where('labour_id', labour.value?.id)
    .where('day', recordForm.form.day)
    .get();

  records.forEach((record) => {
    dailyTime += record.hours;

    if(record.major_break) {
      majorBreak = record.major_break;
    }
  });

  return dailyTime > 3 && !majorBreak;
});

const saveRecord = () => {
  recordForm.resetErrors();

  if(record.value) {
    Swal.fire({
      text: t('Mobile.MobileLabourRecord.whyMakingChange'),
      icon: 'info',
      input: 'text',
      showConfirmButton: true,
      showCancelButton: true,
    }).then((result) => {
      if(result.isConfirmed) {
        recordForm.form.comment = result.value;

        updateRecordAction.request(record.value.id, recordForm.form, { day: recordForm.form.day }).then(() => {
          router.push({ name: 'MobileLabourDashboard', query: { day: dateAsYMD(day.value) } });
        }).catch((error) => {
          recordForm.setErrors(error.data);
        });
      }
    });
  } else {
    if(futureRecord.value) {
      Swal.fire({
        icon: 'warning',
        title: t('Mobile.MobileLabourRecord.futureDateRecord'),
        text: t('Mobile.MobileLabourRecord.saveRecordConfirmation'),
        showCancelButton: true,
      }).then((result) => {
        if(result.isConfirmed) {
          createRecordAction.request(labour.value?.id, recordForm.form).then(() => {
            router.push({ name: 'MobileLabourDashboard', query: { day: dateAsYMD(day.value) } });
          }).catch((error) => {
            recordForm.setErrors(error.data);
          });
        }
      });
    } else {
      createRecordAction.request(labour.value?.id, recordForm.form).then(() => {
        router.push({ name: 'MobileLabourDashboard', query: { day: dateAsYMD(day.value) } });
      }).catch((error) => {
        recordForm.setErrors(error.data);
      });
    }
  }
};

const navStore = useNavStore();

navStore.backRoute = { name: 'MobileLabourDashboard', query: { day: dateAsYMD(day.value) } };

const previousRecord = computed(() => {
  return labourDailyRecordsStore.models
    .where('day', dateAsYMD(day.value))
    .where('labour_id', parseInt(props.labourId))
    .orderBy('id', 'desc')
    .first() ||
    labourDailyRecordsStore.models
      .where('labour_id', parseInt(props.labourId))
      .orderBy('id', 'desc')
      .first();
});

watch(previousRecord, (newValue) => {
  if(newValue && !props.recordId) {
    recordForm.form.area = newValue.area;
    recordForm.form.element = newValue.element;
    recordForm.form.wbs_code_id = newValue.wbs_code_id;
    recordForm.form.activity = newValue.activity;

    if(newValue.day === dateAsYMD(day.value)) {
      recordForm.form.start_at = timeForInput(newValue.end_at);
    }
  }
});

watch([() => projectsStore.project, () => labour], ([newProject, newLabour]) => {
  const previousRecord = newLabour ?
    labourDailyRecordsStore.models
      .where('day', dateAsYMD(day.value))
      .where('labour_id', labour.value?.id)
      .first() :
    undefined;

  if(!previousRecord && newProject) {
    recordForm.form.start_at = newProject.shift_start_at;
    const end = new Date(newProject.shiftStartAt);

    end.setHours(end.getHours() + newProject.labour_standard_shift);

    if(hoursToDate(newProject.shift_start_at).getHours() >= 17) {
      recordForm.form.end_at = dateForInput(end);
    } else {
      recordForm.form.end_at = timeForInput(end);
    }
  }
}, { immediate: true });

const minorBreakMarked = computed(() => {
  let minorBreak = false;

  const records = labourDailyRecordsStore.models
    .where('labour_id', labour.value?.id)
    .where('day', recordForm.form.day)
    .get();

  records.forEach((labourRecord) => {
    if(labourRecord.minor_break && labourRecord.id !== record.value?.id) {
      minorBreak = labourRecord.minor_break;
    }
  });

  return minorBreak;
});

const majorBreakMarked = computed(() => {
  let majorBreak = false;

  const records = labourDailyRecordsStore.models
    .where('labour_id', labour.value?.id)
    .where('day', recordForm.form.day)
    .get();

  records.forEach((labourRecord) => {
    if(labourRecord.major_break && labourRecord.id !== record.value?.id) {
      majorBreak = labourRecord.major_break;
    }
  });

  return majorBreak;
});

const toggleNightShift = ref(false);

watch(toggleNightShift, (newValue) => {
  if(project.value) {
    const shiftStart = newValue ? project.value.night_shift_start_at : project.value.shift_start_at;
    const end = new Date(newValue ? project.value.nightShiftStartAt : project.value.shiftStartAt);

    end.setHours(end.getHours() + project.value.labour_standard_shift);
    recordForm.form.start_at = newValue ? project.value.night_shift_start_at : project.value.shift_start_at;

    nextTick(() => {
      if(hoursToDate(shiftStart).getHours() >= 17) {
        recordForm.form.end_at = dateForInput(end);
      } else {
        recordForm.form.end_at = timeForInput(end);
      }
    });
  }
});

const variationOrders = computed(() => {
  return variationOrdersStore.models.where('project_id', project.value?.id).where(
    'status',
    VariationOrderStatus.approved,
  ).get();
});
</script>
